import styles from "./reduce.module.css";
import { binarySearch, dailyLetters, navigate } from "./utils";
import { requestDay, vibrate } from "../utils";
static interface Letter {
  char: string;
  id: number;
}
static const EmojiNumbers = [
  "0️⃣",
  "1️⃣",
  "2️⃣",
  "3️⃣",
  "4️⃣",
  "5️⃣",
  "6️⃣",
  "7️⃣",
  "8️⃣",
  "9️⃣",
  "🔟",
  "🕚",
  "🕛",
  "🕐",
  "🕑",
  "🕒",
  "🕓",
  "🕔",
  "🕕",
  "🕖",
  "🕗",
];

<lazy-english-dictionary/words/>

<const/day=requestDay($global.request)>

<let/looseLetters=dailyLetters(day, 20).map((char, id) => ({
  char,
  id,
})) as (Letter | null)[]>

<let/_tabStop=0>
<let/tabStop=looseLetters[_tabStop] ? _tabStop : looseLetters.findIndex(Boolean)
  valueChange(slot) {
    _tabStop = slot;
    [...$tile][slot].focus();
  }
>

<let/pendingWord=[] as Letter[]>
<let/submittedWords=[] as Letter[][]>

<let/incorrectGuess=false>

<script>if (incorrectGuess) vibrate([100, 100, 50, 100, 100]);</script>

<const/points=(
  looseLetters.filter(Boolean).length +
  (pendingWord.length && 1) +
  submittedWords.length
)>

<const/addLetter(i: number) {
  if (!looseLetters[i]) {
    throw new Error("Something broke! Attempted to add invalid letter");
  }
  pendingWord = [...pendingWord, looseLetters[i]];
  looseLetters = looseLetters.toSpliced(i, 1, null);
  incorrectGuess = false;
  vibrate(50);
}>

<const/revertLetters(word: Letter[], clearPrevious: () => void) {
  const nextLetters = [...looseLetters];
  let i = 0;
  for (const letter of word) {
    while (nextLetters[i]) i++;
    nextLetters[i++] = letter;
  }
  document.startViewTransition(() => {
    looseLetters = nextLetters;
    clearPrevious();
    incorrectGuess = false;
  });
  const active = document.activeElement as HTMLButtonElement;
  if (![...$tile].includes(active)) active?.blur?.();
}>

<const/submitWord() {
  if (words) {
    if (binarySearch(words, pendingWord.map(({ char }) => char).join(""))) {
      document.startViewTransition(() => {
        submittedWords = [...submittedWords, pendingWord];
        pendingWord = [];
        vibrate(50);
      });
    } else {
      incorrectGuess = true;
    }
  }
}>

<let/helpOpen=false>
<let/resultsOpen=false>

<header class=styles.header>
  <button
    class=styles.iconButton
    title="help"
    onClick() {
      helpOpen = true;
    }
  >
    ?
  </button>
  <strong>${points}<span class=styles.srOnly>${" "}points</span></strong>
  <button
    class=styles.iconButton
    title="shuffle loose letters"
    onClick() {
      document.startViewTransition(() => {
        looseLetters = looseLetters.toSorted(() => Math.random() - 0.5);
      });
    }
  >
    ⇆
  </button>
</header>

<game-dialog open:=helpOpen>
  <@heading>Welcome to Reduce!</@heading>
  <p>Match letters to make words</p>
  <p>Every word and loose letter counts as a point</p>
  <p>Your goal is to <em>minimize</em> points</p>
</game-dialog>

<div class=styles.looseLetters>
  <for|letter, i| of=looseLetters>
    <if=letter>
      <button/$tile
        class=styles.tile
        tabindex=i === tabStop ? 0 : -1
        onClick() {
          const next = navigate(looseLetters, i, "ArrowRight")!;
          tabStop = next === i ? navigate(looseLetters, i, "ArrowLeft")! : next;
          addLetter(i);
        }
        onKeyDown(e) {
          const slot = navigate(looseLetters, i, e.key);
          if (slot !== undefined) {
            e.preventDefault();
            tabStop = slot;
          }
        }
        style={
          "view-transition-name": `letter-${letter.id}`,
          rotate: `${(Math.random() - 0.5) * 5}deg`,
        }
      >
        ${letter.char}
      </button>
    </if>
    <else><span/></else>
  </for>
  <show=!looseLetters.some(Boolean)>
    <button
      class=[styles.iconButton, styles.openSubmit]
      title="share score"
      onClick() {
        submitWord();
        resultsOpen = true;
      }
    >
      ⇪
    </button>
  </show>
</div>

<div class=styles.pendingWord>
  <if=pendingWord.length>
    <button
      class=[styles.pendingLetters, incorrectGuess && styles.incorrectGuess]
      onClick() {
        revertLetters(pendingWord, () => (pendingWord = []));
      }
    >
      <for|letter| of=pendingWord>
        <span
          class=styles.tile
          style={
            "view-transition-name": `letter-${letter.id}`,
            rotate: `${(Math.random() - 0.5) * 2}deg`,
          }
        >
          ${letter.char}
        </span>
      </for>
    </button>
    <span class=styles.actions>
      <button
        class=styles.iconButton
        aria-label="delete last letter"
        onClick() {
          revertLetters(
            [pendingWord.at(-1)!],
            () => (pendingWord = pendingWord.toSpliced(-1)),
          );
        }
      >
        ⌫
      </button>
      <button
        class=styles.iconButton
        aria-label="submit"
        disabled=!words
        onClick() {
          submitWord();
        }
      >
        ⏎
      </button>
    </span>
  </if>
</div>

<div class=styles.words>
  <for|word, i| of=submittedWords>
    <button onClick() {
      revertLetters(
        word,
        () => (submittedWords = submittedWords.toSpliced(i, 1)),
      );
    }>
      <for|letter| of=word>
        <span
          class=styles.tile
          style={
            "view-transition-name": `letter-${letter.id}`,
            rotate: `${(Math.random() - 0.5) * 3}deg`,
          }
        >
          ${letter.char}
        </span>
      </for>
    </button>
  </for>
</div>

<const/loose=[
  ...looseLetters.filter((letter) => letter !== null),
  ...pendingWord,
]>
<const/score=submittedWords.length + loose.length>

<game-dialog open:=resultsOpen>
  <@heading>
    ${day.toLocaleDateString("en-US", { dateStyle: "long", timeZone: "UTC" })}
  </@heading>
  <p class=styles.stat>${score}</p>
  <p class=styles.caption>${score === 1 ? "point" : "points"}</p>

  <share-button text=(
    `Reduce ${day.toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "2-digit",
      timeZone: "UTC",
    })}: ${score} ${score === 1 ? "point" : "points"}

${submittedWords.map((word) => EmojiNumbers[word.length]).join("")}
${"1️⃣".repeat(loose.length)}`
  )/>
</game-dialog>

<script>
  document.addEventListener(
    "keydown",
    (e) => {
      const { key } = e;
      if (/[a-zA-Z]/.test(key) && !e.altKey && !e.ctrlKey && !e.metaKey) {
        const char = key.toLowerCase();
        const i = looseLetters.findIndex((letter) => letter?.char === char);
        if (i >= 0) {
          addLetter(i);
        }
      }
      switch (key) {
        case "Enter":
          if (pendingWord.length) {
            e.preventDefault();
            submitWord();
          }
          break;
        case "Escape":
          e.preventDefault();
          revertLetters(pendingWord, () => (pendingWord = []));
          break;
        case "Backspace":
          if (pendingWord.length) {
            if (e.altKey) {
              revertLetters(pendingWord, () => (pendingWord = []));
            } else {
              revertLetters(
                [pendingWord.at(-1)!],
                () => (pendingWord = pendingWord.toSpliced(-1)),
              );
            }
          }
          break;
      }
    },
    {
      signal: $signal,
    },
  );
</script>
